import type { ColorScheme } from '@vis.gl/react-google-maps';

import { createContext, use, useEffect, useMemo, useState } from 'react';

import type { Theme, ThemeProviderProps, ThemeProviderState } from '.';

export const initialState: ThemeProviderState = {
  theme: 'system',
  googleMapsTheme: 'FOLLOW_SYSTEM',
  setTheme: () => null,
};

export const ThemeContextProvider = createContext<ThemeProviderState | null>(initialState);

export function useTheme() {
  const context = use(ThemeContextProvider);

  if (!context) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
}

export function ThemeProvider(themeProviderProps: ThemeProviderProps) {
  const { children, defaultTheme = 'system', storageKey = 'theme', ...props } = themeProviderProps;
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme | null) || defaultTheme);

  const googleMapsTheme = useMemo<ColorScheme>(() => {
    if (theme === 'dark') {
      return 'DARK';
    } else if (theme === 'light') {
      return 'LIGHT';
    } else {
      return 'FOLLOW_SYSTEM';
    }
  }, [theme]);

  const updateThemeColorMetaTag = (currentTheme: 'dark' | 'light') => {
    const themeColor = currentTheme === 'dark' ? '#000000' : '#ffffff';

    let metaThemeColor = document.querySelector('meta[name="theme-color"]');

    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }

    metaThemeColor.setAttribute('content', themeColor);
  };

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

      root.classList.add(systemTheme);
      updateThemeColorMetaTag(systemTheme);

      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    googleMapsTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeContextProvider {...props} value={value}>
      {children}
    </ThemeContextProvider>
  );
}
